/**
 * Planned — Aim 2 (Reaction-Aware Search & Filtering) Action Definitions
 *
 * Actions: reaction-aware-fragment-search, filter-by-reaction,
 *   filter-by-properties, multi-parameter-visualization,
 *   assemble-compound-with-reaction, lookup-building-block-vendor
 *
 * All actions in this file have status: 'planned' and null execution references.
 *
 * @module gibbs/registry/actions/planned-aim2
 */

import { WorkflowTypes as T } from '../types';
import { param, pre } from '../schema';

// ─── reaction-aware-fragment-search ─────────────────────────────────────────

export const reactionAwareFragmentSearch = {
    id: 'reaction-aware-fragment-search',
    name: 'Reaction-Aware Fragment Search',
    category: 'Planned — Aim 2',
    description: 'Extended fragment search that combines geometric fragment matching with reaction compatibility and building block availability. Starts from an attachment point and bond vector, finds candidate fragments, then checks which building blocks contain them and which reactions can join them. Returns only synthetically accessible fragment modifications.',
    inputs: [
        param('bondVector', T.BondVector, 'Growth direction'),
        param('selectedReactions', 'object[]', 'User-selected allowable reactions'),
        param('propertyFilters', 'object', 'Physicochemical property constraints'),
    ],
    outputs: [
        param('results', T.FragmentSearchResult, 'Ranked fragments with building block and reaction annotations'),
        param('buildingBlocks', 'object[]', 'Compatible building blocks per fragment'),
        param('reactions', 'object[]', 'Applicable reactions per result'),
    ],
    parameters: [
        param('reactionFilter', 'string[]', 'Allowed/disallowed reaction types', false),
        param('propertyThresholds', 'object', 'Property constraints: {MW: {min, max}, LogP: {min, max}, TPSA: {min, max}, HBA: {min, max}, HBD: {min, max}}', false),
        param('vendorFilter', 'string[]', 'Preferred vendors', false),
        param('scope', 'string', 'Search quality', false),
    ],
    dependsOn: ['prepare-vendor-library'],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
        pre('fragment_map_available', 'Fragment maps must be available'),
        pre('fragment_data_available', 'Aim 1 building block database must be populated'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'planned',
    userAction: null,
    serverCommand: null,
    jobType: null,
};

// ─── filter-by-reaction ─────────────────────────────────────────────────────

export const filterByReaction = {
    id: 'filter-by-reaction',
    name: 'Filter Results by Reaction',
    category: 'Planned — Aim 2',
    description: 'Filter fragment search results to only include those compatible with user-selected chemical reactions. Pre-filter incompatible reactions and flag unavailable ones.',
    inputs: [
        param('searchResults', T.FragmentSearchResult, 'Results from fragment search'),
        param('selectedReactions', 'object[]', 'User\'s chosen reactions'),
    ],
    outputs: [
        param('filteredResults', T.FragmentSearchResult, 'Only reaction-compatible results'),
        param('unavailableReactions', 'object[]', 'Reactions with no precomputed building block fragments'),
        param('incompatibleReactions', 'object[]', 'Reactions that cannot apply to the current attachment point'),
    ],
    parameters: [],
    dependsOn: ['reaction-aware-fragment-search'],
    preconditions: [
        pre('fragment_data_available', 'Fragment search results must be available'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'planned',
    userAction: null,
    serverCommand: null,
    jobType: null,
};

// ─── filter-by-properties ───────────────────────────────────────────────────

export const filterByProperties = {
    id: 'filter-by-properties',
    name: 'Multi-Parameter Property Filter',
    category: 'Planned — Aim 2',
    description: 'Filter fragment search results or compound lists by physicochemical property thresholds. Applied during search (not after enumeration) for efficiency. Supports multiple simultaneous property constraints.',
    inputs: [
        param('results', T.FragmentSearchResult, 'Items to filter (FragmentSearchResult[] or CompoundList)'),
        param('thresholds', 'object', 'Min/max constraints per property'),
    ],
    outputs: [
        param('filteredResults', T.FragmentSearchResult, 'Items passing all thresholds'),
    ],
    parameters: [
        param('MW', 'object', 'Molecular weight range {min, max} in Da', false),
        param('LogP', 'object', 'Partition coefficient range {min, max}', false),
        param('TPSA', 'object', 'Topological polar surface area range {min, max} in angstrom squared', false),
        param('HBA', 'object', 'Hydrogen bond acceptors range {min, max}', false),
        param('HBD', 'object', 'Hydrogen bond donors range {min, max}', false),
        param('RotBonds', 'object', 'Rotatable bonds range {min, max}', false),
        param('PSA', 'object', 'Polar surface area range {min, max}', false),
        param('Lipophilicity', 'object', 'Lipophilicity range {min, max}', false),
    ],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'planned',
    userAction: null,
    serverCommand: null,
    jobType: null,
};

// ─── multi-parameter-visualization ──────────────────────────────────────────

export const multiParameterVisualization = {
    id: 'multi-parameter-visualization',
    name: 'Multi-Parameter Property Space Visualizer',
    category: 'Planned — Aim 2',
    description: 'Interactive visualization of fragment/compound results in multi-dimensional property space. Supports scatter plots, contour maps, and property projections. Users can select axis properties, apply thresholds, and explore the chemical space visually.',
    inputs: [
        param('results', T.FragmentSearchResult, 'Items to visualize (FragmentSearchResult[] or CompoundList)'),
        param('properties', 'string[]', 'Properties for axes'),
    ],
    outputs: [
        param('visualization', 'object', 'Interactive chart data'),
    ],
    parameters: [
        param('xProperty', 'string', 'X-axis property', true),
        param('yProperty', 'string', 'Y-axis property', true),
        param('colorProperty', 'string', 'Color-coding property', false),
        param('thresholds', 'object', 'Visual threshold lines', false),
    ],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'planned',
    userAction: null,
    serverCommand: null,
    jobType: null,
};

// ─── assemble-compound-with-reaction ────────────────────────────────────────

export const assembleCompoundWithReaction = {
    id: 'assemble-compound-with-reaction',
    name: 'Assemble Compound via Reaction',
    category: 'Planned — Aim 2',
    description: 'Build a compound by joining a hit compound with building blocks using a specified chemical reaction. The system assembles the full product compound and provides vendor, price, and availability information for the building blocks.',
    inputs: [
        param('hitCompound', T.Compound, 'Starting compound with attachment points'),
        param('buildingBlocks', T.MolData, 'Building blocks to attach (MolData[])'),
        param('reaction', 'object', 'Chemical reaction to use for joining'),
        param('attachmentPoints', 'object[]', 'Where to attach on the hit compound'),
    ],
    outputs: [
        param('assembledCompound', T.Compound, 'Full product compound'),
        param('vendorInfo', 'object', 'Building block source, price, delivery time, availability'),
        param('reactionScheme', 'object', 'Reaction details and synthetic pathway'),
    ],
    parameters: [],
    dependsOn: ['prepare-vendor-library'],
    preconditions: [
        pre('compound_loaded', 'A hit compound must be loaded'),
        pre('fragment_data_available', 'Aim 1 building block database must be populated'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'planned',
    userAction: null,
    serverCommand: null,
    jobType: null,
};

// ─── lookup-building-block-vendor ───────────────────────────────────────────

export const lookupBuildingBlockVendor = {
    id: 'lookup-building-block-vendor',
    name: 'Building Block Vendor Lookup',
    category: 'Planned — Aim 2',
    description: 'Query vendor APIs to check building block availability, pricing, and delivery time. Supports eMolecules and Enamine vendor APIs.',
    inputs: [
        param('buildingBlocks', T.MolData, 'Building blocks to look up (MolData[])'),
    ],
    outputs: [
        param('vendorInfo', 'object[]', 'Per-block vendor data: {vendor, catalogId, price, currency, deliveryTime, inStock}'),
    ],
    parameters: [
        param('vendors', 'string[]', 'Which vendors to query', false, { options: ['emolecules', 'enamine'] }),
    ],
    dependsOn: [],
    preconditions: [
        pre('internet_available', 'Internet access is required'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'planned',
    userAction: null,
    serverCommand: null,
    jobType: null,
};

// ─── Default Export ─────────────────────────────────────────────────────────

export default [
    reactionAwareFragmentSearch,
    filterByReaction,
    filterByProperties,
    multiParameterVisualization,
    assembleCompoundWithReaction,
    lookupBuildingBlockVendor,
];
